import { useEffect, useMemo, useState } from "react";

import { Application } from "@core/Application";
import type { ColaboratorInfo } from "@models/Colaborators";
import type { QontactPointInformation, ShiftRecipeWithUserIDs } from "@models/QontactPoint";
import { ToastManager } from "@shared/designSystem/messages/ToastManager";
import {
    ShiftHeadcount,
    canDecrement,
    decrement,
    freeSlots,
    headcountHint,
    increment,
    parseTypedExpected,
    toggleAssigned,
    toggleAssignedMany,
} from "@utils/ShiftHeadcount";

/** Order shown in the modal (Lun..Dom) with the index the backend stores (0 = domingo) */
export const shiftWeekdays: Array<{ index: number; label: string }> = [
    { index: 1, label: "Lun" },
    { index: 2, label: "Mar" },
    { index: 3, label: "Mié" },
    { index: 4, label: "Jue" },
    { index: 5, label: "Vie" },
    { index: 6, label: "Sáb" },
    { index: 0, label: "Dom" },
];

/** "9:00" (backend) -> "09:00" (HourInput) */
export const toHourInputValue = (time?: string): string => {
    const match = (time ?? "").match(/^(\d{1,2}):(\d{2})$/);
    return match ? `${match[1].padStart(2, "0")}:${match[2]}` : "";
};

const isValidHour = (time: string): boolean => /^([01]\d|2[0-3]):[0-5]\d$/.test(time);

/** Typing mask for the hour fields: digits only, "0930" -> "09:30" */
export const maskHour = (typed: string): string => {
    // "9:30" typed with a one-digit hour -> "09:30"
    const normalized = /^\d:/.test(typed) ? `0${typed}` : typed;
    const digits = normalized.replace(/\D/g, "").slice(0, 4);
    return digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
};

/** "09:00" -> "9:00", the format the shifts are stored with */
const toBackendHour = (time: string): string => {
    const [hour, minute] = time.split(":");
    return `${parseInt(hour, 10)}:${minute}`;
};

const allWeekdays = shiftWeekdays.map((day) => day.index);

/** New shift: nothing marked. Existing daily shift (or weekly without days): all 7 marked. */
const initialWeekdays = (recipe?: ShiftRecipeWithUserIDs): number[] => {
    if (!recipe?.firestoreRecordId) {
        return [];
    }
    const weekdays = recipe.type === "weekly" ? recipe.configurationData?.weekdays : undefined;
    const days = Array.isArray(weekdays) ? weekdays.map(Number) : weekdays ? Object.values(weekdays).map(Number) : [];
    return days.length > 0 ? days : [...allWeekdays];
};

interface ShiftRecipeModalOptions {
    orgID: string;
    qontactPoint: QontactPointInformation;
    /** undefined = new shift */
    recipe?: ShiftRecipeWithUserIDs;
    collaborators: ColaboratorInfo[];
    onSaved: (recipe: ShiftRecipeWithUserIDs, isNew: boolean) => void;
    onClose: () => void;
}

/**
 * Add / edit shift (design 3c): name, hours, days, expected people (− / +) and
 * optional people assigned by name. Expected is always >= assigned.
 */
export const useShiftRecipeModalViewModel = ({
    orgID,
    qontactPoint,
    recipe,
    collaborators,
    onSaved,
    onClose,
}: ShiftRecipeModalOptions) => {
    const isNew = !recipe?.firestoreRecordId;

    const [name, setName] = useState(recipe?.name ?? "");
    // New shifts start empty (placeholder 00:00)
    const [hourFrom, setHourFrom] = useState(toHourInputValue(recipe?.hourFrom));
    const [hourTo, setHourTo] = useState(toHourInputValue(recipe?.hourTo));
    const [weekdays, setWeekdays] = useState<number[]>(initialWeekdays(recipe));
    // New shifts start at 0 with nobody assigned
    const [headcount, setHeadcount] = useState<ShiftHeadcount>(() => {
        const assignedIDs = Array.from(new Set(recipe?.userIDs ?? []));
        return { expected: Math.max(Number(recipe?.expected ?? 0), assignedIDs.length), assignedIDs };
    });
    const [expectedInput, setExpectedInput] = useState(String(headcount.expected));
    const [bumped, setBumped] = useState(false);
    const [lowered, setLowered] = useState(false);
    const [search, setSearch] = useState("");
    const [errors, setErrors] = useState({ name: "", hours: "", days: "", expected: "" });
    const [isSaving, setIsSaving] = useState(false);

    // Keep the input in sync when − / + or the list change the total
    useEffect(() => {
        setExpectedInput(String(headcount.expected));
    }, [headcount.expected]);

    const typedExpected = expectedInput.trim() === "" ? NaN : Number(expectedInput);
    const isTypedBelowAssigned = !Number.isNaN(typedExpected) && typedExpected < headcount.assignedIDs.length;

    const collaboratorsByID = useMemo(
        () => new Map(collaborators.map((collaborator) => [collaborator.id, collaborator])),
        [collaborators]
    );

    const filteredCollaborators = useMemo(() => {
        const query = search.trim().toLowerCase();
        const list = query
            ? collaborators.filter((collaborator) => collaborator.name?.toLowerCase().includes(query))
            : collaborators;
        return [...list].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    }, [collaborators, search]);

    // Every active collaborator assigned (e.g. "Seleccionar todos" without search):
    // one summary pill instead of one per person
    const allCollaboratorIDs = collaborators.map((collaborator) => collaborator.id);
    const areAllCollaboratorsAssigned =
        allCollaboratorIDs.length > 0 && allCollaboratorIDs.every((id) => headcount.assignedIDs.includes(id));

    /** "Todos" pill ×: unassigns everyone, like unchecking "Seleccionar todos" */
    const unassignAll = () => {
        const result = toggleAssignedMany(headcount, allCollaboratorIDs);
        setHeadcount(result.headcount);
        setBumped(result.bumped);
        setLowered(result.lowered);
    };

    const assignedCollaborators = headcount.assignedIDs.map((id) => ({
        id,
        // Someone deactivated after being assigned isn't in the active list
        name: collaboratorsByID.get(id)?.name ?? "Colaborador".localized(),
    }));

    const onExpectedTyped = (value: string) => {
        setExpectedInput(value.replace(/\D/g, ""));
        setBumped(false);
        setLowered(false);
        const parsed = parseTypedExpected(value, headcount.assignedIDs.length);
        if (parsed !== null) {
            setHeadcount((current) => ({ ...current, expected: parsed }));
            setErrors((current) => ({ ...current, expected: "" }));
        }
    };

    // Leaving the field goes back to the last valid total
    const onExpectedBlur = () => setExpectedInput(String(headcount.expected));

    const toggleCollaborator = (userID: string) => {
        const result = toggleAssigned(headcount, userID);
        setHeadcount(result.headcount);
        setBumped(result.bumped);
        setLowered(result.lowered);
        setErrors((current) => ({ ...current, expected: "" }));
    };

    const isEveryDay = allWeekdays.every((day) => weekdays.includes(day));

    const visibleIDs = filteredCollaborators.map((collaborator) => collaborator.id);
    const areAllVisibleAssigned = visibleIDs.length > 0 && visibleIDs.every((id) => headcount.assignedIDs.includes(id));

    /** "Seleccionar todos": acts on the collaborators the search shows */
    const toggleAllVisible = () => {
        const result = toggleAssignedMany(headcount, visibleIDs);
        setHeadcount(result.headcount);
        setBumped(result.bumped);
        setLowered(result.lowered);
        setErrors((current) => ({ ...current, expected: "" }));
    };

    const toggleWeekday = (index: number) => {
        setWeekdays((current) => current.includes(index) ? current.filter((day) => day !== index) : [...current, index]);
        setErrors((current) => ({ ...current, days: "" }));
    };

    /** "Todos los días": marks the 7 days, or unmarks them if they already were */
    const toggleEveryDay = () => {
        setWeekdays(isEveryDay ? [] : [...allWeekdays]);
        setErrors((current) => ({ ...current, days: "" }));
    };

    const validate = () => {
        const nextErrors = {
            name: name.trim() ? "" : "%s es obligatorio".localized("Nombre del turno".localized()),
            hours: !isValidHour(hourFrom) || !isValidHour(hourTo)
                ? "Completá Desde y Hasta con el formato HH:MM.".localized()
                : hourFrom === hourTo
                    ? "El horario de inicio y fin no puede ser el mismo.".localized()
                    : "",
            days: weekdays.length > 0 ? "" : "Elegí al menos un día.".localized(),
            expected: headcount.expected >= 1 ? "" : "El turno tiene que esperar al menos 1 persona.".localized(),
        };
        setErrors(nextErrors);
        return !Object.values(nextErrors).some(Boolean);
    };

    const save = async () => {
        if (isSaving || !validate()) {
            return;
        }
        const sortedWeekdays = [...weekdays].sort((a, b) => a - b);
        // 7 days = daily, as the backend stores it today
        const shiftsService = Application.shared().coreDependencies.shiftsService;

        const payload: ShiftRecipeWithUserIDs = {
            ...(recipe ?? {
                tolerance: 0.5,
                expirationType: "never",
                startTimestamp: Date.now(),
                functionalUnitID: "",
            }),
            buildingID: recipe?.buildingID ?? qontactPoint.buildingID,
            functionalUnitID: recipe?.functionalUnitID ?? "",
            name: name.trim(),
            hourFrom: toBackendHour(hourFrom),
            hourTo: toBackendHour(hourTo),
            type: isEveryDay ? "daily" : "weekly",
            configurationData: isEveryDay ? {} : { weekdays: sortedWeekdays },
            expected: headcount.expected,
            userIDs: headcount.assignedIDs,
        } as ShiftRecipeWithUserIDs;

        setIsSaving(true);
        try {
            if (isNew) {
                const created = await shiftsService.createShiftRecipe(orgID, {
                    ...payload,
                    qontactPointID: qontactPoint.id,
                });
                onSaved(
                    { ...payload, functionalUnitID: created?.functionalUnitID ?? "", firestoreRecordId: created?.firestoreRecordId },
                    true
                );
            } else {
                await shiftsService.updateShiftRecipe(orgID, recipe!.firestoreRecordId!, payload);
                onSaved(payload, false);
            }
            ToastManager.shared().showSuccessMessage("Turno guardado".localized());
            onClose();
        } catch (error) {
            console.error(error);
            ToastManager.shared().showErrorMessage("No se pudo guardar el turno".localized());
        } finally {
            setIsSaving(false);
        }
    };

    return {
        isNew,
        name,
        setName: (value: string) => {
            setName(value);
            setErrors((current) => ({ ...current, name: "" }));
        },
        hourFrom,
        setHourFrom: (value: string) => {
            setHourFrom(maskHour(value));
            setErrors((current) => ({ ...current, hours: "" }));
        },
        hourTo,
        setHourTo: (value: string) => {
            setHourTo(maskHour(value));
            setErrors((current) => ({ ...current, hours: "" }));
        },
        weekdays,
        toggleWeekday,
        isEveryDay,
        toggleEveryDay,
        expectedInput,
        onExpectedTyped,
        onExpectedBlur,
        isTypedBelowAssigned,
        assignedCount: headcount.assignedIDs.length,
        freeSlots: freeSlots(headcount),
        progress: headcount.expected > 0 ? headcount.assignedIDs.length / headcount.expected : 0,
        hint: headcountHint(headcount, bumped, lowered),
        canDecrement: canDecrement(headcount),
        decrementExpected: () => {
            setHeadcount(decrement);
            setBumped(false);
            setLowered(false);
        },
        incrementExpected: () => {
            setHeadcount(increment);
            setBumped(false);
            setLowered(false);
            setErrors((current) => ({ ...current, expected: "" }));
        },
        search,
        setSearch,
        filteredCollaborators,
        isAssigned: (userID: string) => headcount.assignedIDs.includes(userID),
        toggleCollaborator,
        areAllVisibleAssigned,
        toggleAllVisible,
        assignedCollaborators,
        areAllCollaboratorsAssigned,
        unassignAll,
        errors,
        isSaving,
        save,
    };
};
